import { CampaignPhase, CampaignPlanRequestDTO, PostStatus, SocialCampaignDTO } from 'models/socialCampaign'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import {
  SocialCampaignTarget,
  postsNotPublishableOf,
  useApproveAllSocialCampaignPosts,
  useGenerateSocialCampaignPosts
} from 'dataQueries/socialCampaign.query'
import { failurePropertiesOf, refusalPropertiesOf, trackSocialCampaignEvent, wentLive } from 'utils/analytics/socialCampaign'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { actionBarStateOf } from 'utils/socialCampaign/schedule'
import { logger } from 'utils/logging'
import { useCampaignFlash } from './CampaignFlash.context'
import { useTranslation } from 'react-i18next'

/** How long the first post of a new wave is looked for (its answer renders); past this a later render leaves it be. */
export const FOCUS_NEW_POST_WITHIN_MS = 2_000

interface Options {
  /** A refused or failed action: say so and show the campaign as the backend has it */
  onActionError: () => void
  /** Re-reads the campaign after a refusal the page names itself */
  refetchCampaign: () => void
  /** The header's title takes the focus */
  focusTitle: () => void
  /** The tab, where the rows are */
  rootRef: RefObject<HTMLElement>
  titleRef: RefObject<HTMLElement>
  /** The page's clock, which the bar reads its drafts against (`actionBarStateOf`) */
  now: Date
}

/**
 * The schedule's actions (ISSUE-3623): Approve all, and Launch campaign once every draft is approved (prototype v3
 * `approveAll`), and Generate new posts with its dialog (`gen-go`). Each tells the outcome in the flash.
 */
export function useScheduleActions(target: SocialCampaignTarget, campaign: SocialCampaignDTO | null | undefined, options: Options) {
  const { onActionError, refetchCampaign, focusTitle, rootRef, titleRef, now } = options
  const { t } = useTranslation(['social_campaign'])
  const { showFlash } = useCampaignFlash()
  const { mutate: approveAll, isPending: approving } = useApproveAllSocialCampaignPosts()
  const { mutate: generate, isPending: generating } = useGenerateSocialCampaignPosts()
  const [generateOpen, setGenerateOpen] = useState(false)
  // One approval at a time: the closing confirmation stays clickable through its fade-out, and `approving` only
  // renders after the click, so a double click would send two
  const approvalOnItsWay = useRef(false)
  // the first post of a new wave, brought into view once the answer is rendered
  const [focusPostId, setFocusPostId] = useState<string | null>(null)

  // A campaign ended elsewhere unmounts the dialog without a cancel: closed for good, or a reopening would bring it back
  const ended = campaign?.phase === CampaignPhase.ENDED
  useEffect(() => {
    if (ended) setGenerateOpen(false)
  }, [ended])

  // In view, and focused when its row opens the editor. A static row (no editor yet) cannot hold the focus: the
  // title takes it, without scrolling back up.
  // A row folded or filtered out is not looked for for ever: a later change of the campaign would jump to it.
  useEffect(() => {
    if (!focusPostId) return
    const row = rootRef.current?.querySelector<HTMLElement>(`[data-post="${focusPostId}"]`)
    if (!row) {
      const giveUp = setTimeout(() => setFocusPostId(null), FOCUS_NEW_POST_WITHIN_MS)
      return () => clearTimeout(giveUp)
    }
    if (row instanceof HTMLButtonElement) row.focus({ preventScroll: true })
    else titleRef.current?.focus({ preventScroll: true })
    if (typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'center' })
    setFocusPostId(null)
  }, [focusPostId, campaign, rootRef, titleRef])

  /**
   * Every draft scheduled, a draft campaign live, then the header's title takes the focus. A refusal (422) names
   * every post that cannot go out as it is, those the click did not touch included (one approved earlier without text).
   * The posts reserved for an order are announced on the launch only: a later wave's approval leaves them as they were.
   */
  const approveAllDrafts = useCallback(() => {
    if (!campaign || approvalOnItsWay.current) return
    approvalOnItsWay.current = true
    const draftPosts = campaign.posts.filter((post) => post.status === PostStatus.DRAFT)
    const drafts = new Set(draftPosts.map((post) => post.id))
    const subjects = new Map(campaign.posts.map((post) => [post.id, post.subject]))
    const launching = campaign.phase === CampaignPhase.DRAFT
    // as the bar showed them: a launched campaign's draft whose time passed reads not published, out of its count
    const bar = actionBarStateOf(campaign, now)
    const shown = bar?.kind === 'review' ? bar : { approved: draftPosts.filter((post) => post.reviewed).length, drafts: draftPosts.length }
    const tracked = {
      // the bar's Launch campaign once every draft it counts is approved, Approve all before
      control: shown.approved === shown.drafts ? 'launch' as const : 'approve_all' as const,
      drafts: shown.drafts,
      approvedBefore: shown.approved,
    }
    approveAll(target, {
      onSuccess: (answer) => {
        const scheduled = answer.posts.filter((post) => drafts.has(post.id) && post.status === PostStatus.SCHEDULED).length
        const waiting = launching ? answer.posts.filter((post) => post.status === PostStatus.WAITING_FOR_CONTENT).length : 0
        trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_APPROVE_ALL_SUBMITTED, target, campaign, {
          ...tracked,
          outcome: 'ok',
          scheduled,
          waiting,
          launched: wentLive(campaign, answer),
        })
        showFlash({ kind: 'launched', scheduled, waiting })
        focusTitle()
      },
      onError: (error) => {
        const refused = postsNotPublishableOf(error)
        trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_APPROVE_ALL_SUBMITTED, target, campaign, {
          ...tracked,
          ...failurePropertiesOf(error),
          launched: false,
          ...refusalPropertiesOf(refused),
        })
        if (!refused) return onActionError()
        // a stale page or a contract slip: the refusal is kept out of Sentry, so say it here (ids only)
        const unknown = refused.posts.filter((post) => !subjects.has(post.postId)).map((post) => post.postId)
        if (unknown.length) {
          logger.warn('Social campaign refusal names posts the page does not hold', undefined, {
            context: { component: 'useScheduleActions', action: 'approveAll', metadata: { campaignId: campaign.id, postIds: unknown } },
          })
        }
        showFlash({
          kind: 'not_publishable',
          posts: refused.posts.map((post) => ({ subject: subjects.get(post.postId) ?? t('flash.unknown_post'), reasons: post.reasons })),
        })
        focusTitle()
        refetchCampaign()
      },
      onSettled: () => {
        approvalOnItsWay.current = false
      },
    })
  }, [approveAll, campaign, focusTitle, now, onActionError, refetchCampaign, showFlash, t, target])

  /** The new wave arrives as drafts; its first one is brought into view. */
  const generatePosts = useCallback((plan: CampaignPlanRequestDTO) => {
    if (!campaign) return
    const before = new Set(campaign.posts.map((post) => post.id))
    const tracked = { requestedPosts: plan.posts, weeks: plan.weeks }
    generate({ ...target, body: plan }, {
      onSuccess: (answer) => {
        setGenerateOpen(false)
        const added = answer.posts.filter((post) => !before.has(post.id))
        trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATE_SUBMITTED, target, campaign, { ...tracked, outcome: 'ok', added: added.length })
        if (!added.length) {
          logger.warn('Social campaign generation answered without a new draft', undefined, {
            context: { component: 'useScheduleActions', action: 'generatePosts', metadata: { campaignId: answer.id, requested: plan.posts } },
          })
        }
        showFlash({ kind: 'generated', count: added.length })
        if (added.length) setFocusPostId(added[0].id)
        else focusTitle()
      },
      onError: (error) => {
        trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATE_SUBMITTED, target, campaign, { ...tracked, ...failurePropertiesOf(error) })
        setGenerateOpen(false)
        onActionError()
      },
    })
  }, [campaign, focusTitle, generate, onActionError, showFlash, target])

  return {
    approveAllDrafts,
    generatePosts,
    generateOpen,
    openGenerate: useCallback(() => setGenerateOpen(true), []),
    closeGenerate: useCallback(() => setGenerateOpen(false), []),
    /** An approval or a generation is on its way */
    pending: approving || generating,
    generating,
  }
}
